import { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ImageUpload({ value, onChange, label, className = '' }) {
  const [uploading, setUploading] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState(null);
  const fileInputRef = useRef(null);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploading(true);
    setCompressionInfo(null);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/upload?compress=true`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token && { Authorization: `Bearer ${token}` })
        }
      });

      onChange(res.data.url);
      
      // Show compression info
      if (res.data.compression_ratio > 0) {
        setCompressionInfo({
          original: res.data.original_size,
          compressed: res.data.compressed_size,
          ratio: res.data.compression_ratio
        });
        toast.success(`Image compressed by ${res.data.compression_ratio}%`);
      } else {
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange('');
    setCompressionInfo(null);
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="text-sm font-medium text-slate-700">{label}</label>
      )}
      
      <div className="relative">
        {value ? (
          <div className="relative group">
            <div className="w-full h-32 rounded-lg border border-slate-200 overflow-hidden bg-slate-50">
              <img
                src={value}
                alt="Uploaded"
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={handleRemove}
              data-testid="remove-image-btn"
            >
              <X className="h-4 w-4" />
            </Button>
            {compressionInfo && (
              <div className="absolute bottom-2 left-2 right-2 bg-emerald-500/90 text-white text-[0.65rem] px-2 py-1 rounded flex items-center gap-1">
                <Check className="h-3 w-3" />
                <span>
                  {formatBytes(compressionInfo.original)} → {formatBytes(compressionInfo.compressed)} 
                  ({compressionInfo.ratio}% saved)
                </span>
              </div>
            )}
          </div>
        ) : (
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`
              w-full h-32 rounded-lg border-2 border-dashed border-slate-200 
              flex flex-col items-center justify-center gap-2 
              cursor-pointer hover:border-[#0056A6] hover:bg-blue-50/50 
              transition-all ${uploading ? 'pointer-events-none opacity-50' : ''}
            `}
            data-testid="upload-area"
          >
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 text-[#0056A6] animate-spin" />
                <span className="text-sm text-slate-500">Compressing & uploading...</span>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                  <Upload className="h-5 w-5 text-[#0056A6]" />
                </div>
                <span className="text-sm text-slate-500">Click to upload image</span>
                <span className="text-xs text-slate-400">Auto-compressed for best performance</span>
              </>
            )}
          </div>
        )}
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input"
        />
      </div>
    </div>
  );
}
